import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetAgentDashboardSummaryResponse,
  AgentEvents,
  DashboardReadStatusChangedEvent,
} from '@dreamai/types';
import api from '../../api';
import { generateColorVariants } from './sidebar/threadUtils';
import ErrorState from '../common/ErrorState';
import { ReactComponent as NotificationIndicator } from '../../icons/notification-indicator.svg';
import { usePusher } from '../../contexts/PusherContext';

import './agent-update.scss';

interface AgentUpdateProps {
  agentId: string;
  agentName: string;
  predominantColour?: string;
}

const AgentUpdate: React.FC<AgentUpdateProps> = ({
  agentId,
  agentName,
  predominantColour,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const companyChannel = pusherContext?.pusherChannel;
  const [summary, setSummary] =
    useState<GetAgentDashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wasUnreadOnLoad, setWasUnreadOnLoad] = useState(false);

  // Generate color variants from agent's predominant color
  const colorVariants = predominantColour
    ? generateColorVariants(predominantColour)
    : { base: '#97D5FC', darker: '#4A90C2', lighter: '#E3F2FD' };

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Check if unread before fetching
      const unreadResponse = await api.get<{
        success: boolean;
        has_unread: boolean;
      }>(`/agent-dashboard/has-unread?agentId=${agentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const hadUnread = unreadResponse.data.has_unread;

      const response = await api.get<GetAgentDashboardSummaryResponse>(
        `/agent-dashboard/${agentId}/summary`,
      );

      setSummary(response.data);

      // Only mark as read if there was something unread
      if (hadUnread) {
        setWasUnreadOnLoad(true);
        try {
          await api.post('/agent-dashboard/mark-read', { agentId });
        } catch (markReadErr) {
          console.error('Error marking dashboard as read:', markReadErr);
          // Don't fail the whole component if mark-read fails
        }
      }
    } catch (err) {
      console.error('Error fetching agent summary:', err);
      setError('Failed to load update');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset wasUnreadOnLoad when agent changes
    setWasUnreadOnLoad(false);
    fetchSummary();
  }, [agentId, getAccessTokenSilently]);

  // Listen for company channel events to reload summary when updated
  useEffect(() => {
    if (!companyChannel) return;

    const handleDashboardUpdate = (data: DashboardReadStatusChangedEvent) => {
      // Only reload if this is for our agent and it's a new/updated summary
      if (data.agentId === agentId && data.hasUnread) {
        console.log('Dashboard summary updated, reloading...');
        fetchSummary();
      }
    };

    companyChannel.bind(
      AgentEvents.DASHBOARD_READ_STATUS_CHANGED,
      handleDashboardUpdate,
    );

    return () => {
      companyChannel.unbind(
        AgentEvents.DASHBOARD_READ_STATUS_CHANGED,
        handleDashboardUpdate,
      );
    };
  }, [companyChannel, agentId]);

  if (loading) {
    return <div className='update-loading'></div>;
  }

  if (error) {
    return <ErrorState title='Failed to load update' subtitle={error} />;
  }

  // Check if we have a summary
  // Note: summary is the full response object with a nested summary property
  if (!summary || !summary.summary) {
    return (
      <div className='agent-update-area'>
        <div
          className='agent-update-inner'
          style={{
            background: `linear-gradient(90deg, ${colorVariants.lighter} 0%, ${colorVariants.base} 100%)`,
            borderColor: colorVariants.base,
          }}
        >
          <h6 style={{ color: colorVariants.darker }}>From {agentName}</h6>
          <p>
            I haven't yet written an update, this happens every morning so check
            back soon!
          </p>
        </div>
      </div>
    );
  }

  const summaryDate = new Date(summary.summary.createdAt);
  const timeString = summaryDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className='agent-update-area'>
      <div
        className='agent-update-inner'
        style={{
          background: `linear-gradient(90deg, ${colorVariants.lighter} 0%, ${colorVariants.base} 100%)`,
          borderColor: colorVariants.base,
        }}
      >
        <h6 style={{ color: colorVariants.darker }}>
          <span>
            <NotificationIndicator /> From {agentName}
          </span>
          {wasUnreadOnLoad && <div className='updated-tag'>Updated</div>}
        </h6>
        <p>{summary.summary.text}</p>
      </div>
      <div className='small'>
        Updated at {timeString}, I write this before 6am every day.
      </div>
    </div>
  );
};

export default AgentUpdate;
